import { PAGE_VIEW_EVENT } from '../router/swup.js';

/*
 * headroom-header — Level 2 (CSP build) persistent-chrome sticky header with a
 * three-state appearance machine (transparent / overlay / solid) plus
 * hide-on-scroll-down / show-on-scroll-up.
 *
 * LIBRARY TIER (inert): this component is NOT wired into the kit's default
 * header (resources/views/partials/_header.antlers.html). Its CSS
 * (resources/css/components/_headroom-header.css) is imported only by docs.css,
 * never production app.css — mirroring the inert block-library tier
 * (ADR 2026-06-02-pattern-library-tiers). The Alpine.data('headroomHeader', …)
 * registration ships in the bundle so its /_docs/components demo route works and
 * opt-in is one step, but nothing production renders the partial. Opt in per
 * project by rendering resources/views/components/interactive/headroom-header.antlers.html
 * as the header, adding `@import "./components/_headroom-header.css";` to app.css,
 * and placing a single `[data-header-sentinel]` marker at the point where the
 * header should turn solid (typically the bottom of the hero).
 *
 * PERSISTENT CHROME. The header renders OUTSIDE `#swup`, so Alpine's
 * per-navigation destroyTree/initTree (which only runs on `#swup`) never tears it
 * down: init() runs ONCE at boot and the instance lives for the document
 * lifetime. That is exactly why it must re-sync on every Swup navigation — the
 * per-page sentinel lives INSIDE `#swup` and is replaced on each visit, so the
 * IntersectionObserver would otherwise be left observing a detached node (the
 * stale-sentinel bug). It listens for the canonical PAGE_VIEW_EVENT
 * ('psd:page-view', dispatched from the Swup bridge on every content swap AND on
 * the initial load) to disconnect, re-query `[data-header-sentinel]` in the fresh
 * DOM, and re-observe.
 *
 * COMPOSITOR DISCIPLINE. Layout is read (offsetHeight) ONCE per (re)sync/resize
 * in _measure() and cached. The state machine is driven purely by
 * IntersectionObserver — the callbacks read only entry.isIntersecting, never
 * forcing layout. The scroll handler reads only window.scrollY (rAF-throttled)
 * and never touches element geometry. Hide/show is a compositor-only
 * translateY class toggle; the state colour change is a discrete transition, not
 * scroll-linked. Feature-detected throughout (IntersectionObserver, matchMedia
 * reduced-motion); with no JS the partial renders a plain solid header.
 *
 * Registered in resources/js/app.js as: Alpine.data('headroomHeader', headroomHeader)
 */

/** Minimum scroll delta (px) before the hide/show direction may flip — hysteresis
 *  so sub-pixel jitter / tiny scrolls don't thrash the header. */
const DIRECTION_THRESHOLD = 6;

/** Bottom rootMargin (px) that extends each observer's root box far below the
 *  viewport, turning `isIntersecting` into a durable "the sentinel is at or below
 *  this line" latch (see _observeLine). */
const BELOW_LINE_EXTENT = 100000;

export default function headroomHeader() {
    return {
        _ioHeaderLine: null,
        _ioOverlayLine: null,
        _onScroll: null,
        _onResize: null,
        _onPageView: null,
        _rafId: null,

        // Cached layout (set in _measure(); never read during scroll).
        _headerHeight: 0,
        _reduceMotion: false,

        // Scroll direction / visibility state.
        _lastScrollY: 0,
        _hidden: false,

        // Latched intersection booleans fed by the two observers.
        _belowHeaderLine: false,
        _belowOverlayLine: false,

        init() {
            this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            this._lastScrollY = Math.max(0, window.scrollY);

            this._onScroll = () => this._requestTick();
            window.addEventListener('scroll', this._onScroll, { passive: true });

            // Header height (hence the observer trigger lines) can change on resize.
            this._onResize = () => this._syncSentinel();
            window.addEventListener('resize', this._onResize, { passive: true });

            // Persistent-chrome contract: re-acquire the per-page sentinel after
            // every Swup content swap (and on the initial-load event). Without this
            // the observers keep watching the previous page's detached sentinel.
            this._onPageView = () => this._syncSentinel();
            window.addEventListener(PAGE_VIEW_EVENT, this._onPageView);

            this._syncSentinel();
        },

        /**
         * Cache the header height. Reads layout — only ever called from
         * _syncSentinel() (init / page-view / resize), never during scroll.
         */
        _measure() {
            this._headerHeight = this.$root.offsetHeight || 0;
        },

        /**
         * Re-acquire the per-page `[data-header-sentinel]` and rebuild the two
         * IntersectionObservers around the current header height. With no sentinel
         * (or no IO support) the header falls back to the solid state.
         */
        _syncSentinel() {
            this._disconnectObservers();
            this._measure();

            const sentinel = document.querySelector('[data-header-sentinel]');

            if (!sentinel || !('IntersectionObserver' in window)) {
                // No hero region to be transparent/overlay over → solid.
                this._belowHeaderLine = false;
                this._belowOverlayLine = false;
                this._applyState();
                return;
            }

            // The overlay band is one header height of scroll during which the hero
            // fades under the header before it goes fully solid.
            const overlayLine = this._headerHeight * 2;

            this._ioHeaderLine = this._observeLine(sentinel, this._headerHeight, (below) => {
                this._belowHeaderLine = below;
                this._applyState();
            });
            this._ioOverlayLine = this._observeLine(sentinel, overlayLine, (below) => {
                this._belowOverlayLine = below;
                this._applyState();
            });
        },

        /**
         * Observe `sentinel` against a single horizontal line `linePx` from the top
         * of the viewport. The root box is extended far below the viewport
         * (BELOW_LINE_EXTENT) so `isIntersecting` means, durably, "the sentinel sits
         * at or below the line" — a latch that survives between scroll frames rather
         * than a one-frame crossing event. Reading only `isIntersecting` keeps this
         * off the layout path.
         *
         * @param {Element} sentinel
         * @param {number} linePx — distance of the trigger line from the viewport top
         * @param {(below: boolean) => void} onChange
         * @returns {IntersectionObserver}
         */
        _observeLine(sentinel, linePx, onChange) {
            const io = new IntersectionObserver(
                (entries) => {
                    onChange(entries[entries.length - 1].isIntersecting);
                },
                {
                    root: null,
                    rootMargin: `-${linePx}px 0px ${BELOW_LINE_EXTENT}px 0px`,
                    threshold: 0,
                },
            );
            io.observe(sentinel);
            return io;
        },

        /**
         * Resolve the appearance state from the two latches and reflect it on the
         * root as `data-header-state`. transparent (over the hero) → overlay (hero
         * scrolling under the header) → solid (past the hero / no sentinel).
         */
        _applyState() {
            let state;
            if (!this._belowHeaderLine) {
                state = 'solid';
            } else if (this._belowOverlayLine) {
                state = 'transparent';
            } else {
                state = 'overlay';
            }

            if (this.$root.dataset.headerState !== state) {
                this.$root.dataset.headerState = state;
            }
        },

        /** rAF-throttle the scroll handler: at most one window.scrollY read per frame. */
        _requestTick() {
            if (this._rafId !== null) {
                return;
            }
            this._rafId = requestAnimationFrame(() => {
                this._rafId = null;
                this._updateVisibility();
            });
        },

        /**
         * Hide-on-scroll-down / show-on-scroll-up with direction hysteresis. Reads
         * only window.scrollY (no element geometry) and toggles a compositor-only
         * translateY class. Always visible near the top; never hides for
         * reduced-motion users.
         */
        _updateVisibility() {
            const y = Math.max(0, window.scrollY);

            // Never hide while the header is at/over the top (its own height of scroll).
            if (y <= this._headerHeight) {
                this._setHidden(false);
                this._lastScrollY = y;
                return;
            }

            // Reduced motion: keep the header put — no hide/show translation.
            if (this._reduceMotion) {
                this._setHidden(false);
                this._lastScrollY = y;
                return;
            }

            const dy = y - this._lastScrollY;

            // Hysteresis: ignore tiny scrolls (don't flip, don't advance the anchor)
            // so a small wobble can't repeatedly toggle the header.
            if (Math.abs(dy) < DIRECTION_THRESHOLD) {
                return;
            }

            this._setHidden(dy > 0);
            this._lastScrollY = y;
        },

        _setHidden(hidden) {
            if (hidden === this._hidden) {
                return;
            }
            this._hidden = hidden;
            this.$root.classList.toggle('is-hidden', hidden);
        },

        _disconnectObservers() {
            if (this._ioHeaderLine) {
                this._ioHeaderLine.disconnect();
                this._ioHeaderLine = null;
            }
            if (this._ioOverlayLine) {
                this._ioOverlayLine.disconnect();
                this._ioOverlayLine = null;
            }
        },

        destroy() {
            this._disconnectObservers();
            if (this._rafId !== null) {
                cancelAnimationFrame(this._rafId);
                this._rafId = null;
            }
            if (this._onScroll) {
                window.removeEventListener('scroll', this._onScroll);
                this._onScroll = null;
            }
            if (this._onResize) {
                window.removeEventListener('resize', this._onResize);
                this._onResize = null;
            }
            if (this._onPageView) {
                window.removeEventListener(PAGE_VIEW_EVENT, this._onPageView);
                this._onPageView = null;
            }
        },
    };
}
